import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Empty } from "@/components/UIKit";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Sparkles } from "lucide-react";

const axis = { stroke: "#3f3f46", fontSize: 10, tickLine: false, axisLine: false };
const tooltip = {
  contentStyle: { background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" }, itemStyle: { color: "#fafafa" },
};

const cheapestHours = (hours, n = 3) => new Set([...hours].sort((a, b) => a.ore - b.ore).slice(0, n).map((h) => h.hour));

export const SpotPriceCard = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(1);

  useEffect(() => {
    api.get("/portal/spot-prices").then((r) => {
      setData(r.data);
      if (!r.data.days[1]?.hours.length) setTab(0);
    }).catch(() => setData({ days: [] }));
  }, []);

  if (!data) return null;
  const day = data.days[tab];
  const hours = day?.hours || [];
  const cheap = cheapestHours(hours);
  const min = hours.length ? hours.reduce((a, b) => (b.ore < a.ore ? b : a)) : null;
  const max = hours.length ? hours.reduce((a, b) => (b.ore > a.ore ? b : a)) : null;

  return (
    <Card className="p-5 mb-4" testid="portal-spot-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Strømpris time for time · {data.area}</div>
          <div className="font-heading text-xl text-zinc-50 mt-0.5">{day?.label || "Spotpris"}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Spotpris øre/kWh {data.vat_included ? "inkl. mva" : "(mva-fritatt)"}, uten påslag og nettleie</div>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-zinc-950 border border-zinc-900">
          {data.days.map((d, i) => (
            <button key={d.date} onClick={() => setTab(i)} disabled={!d.hours.length} data-testid={`spot-tab-${i}`}
              className={`px-3 py-1 rounded-md text-xs transition-colors disabled:opacity-30 ${i === tab ? "bg-emerald-500/15 text-emerald-300" : "text-zinc-400 hover:text-zinc-200"}`}>
              {d.label}
            </button>
          ))}
        </div>
      </div>
      {hours.length === 0 ? <Empty title="Morgendagens priser publiseres rundt kl. 13" /> : (
        <>
          <div className="flex flex-wrap gap-4 mb-3 text-xs">
            <span className="flex items-center gap-1.5 text-emerald-400" data-testid="spot-cheapest">
              <Sparkles className="h-3.5 w-3.5" /> Billigst kl. {min.hour}–{String(Number(min.hour) + 1).padStart(2, "0")}: {min.ore} øre
            </span>
            <span className="text-orange-400" data-testid="spot-most-expensive">Dyrest kl. {max.hour}: {max.ore} øre</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hours}>
              <XAxis dataKey="hour" {...axis} />
              <YAxis {...axis} width={32} />
              <Tooltip {...tooltip} formatter={(v) => [`${v} øre/kWh`, ""]} labelFormatter={(h) => `Kl. ${h}:00`} cursor={{ fill: "#18181b" }} />
              <Bar dataKey="ore" radius={[3, 3, 0, 0]}>
                {hours.map((h, i) => <Cell key={i} fill={cheap.has(h.hour) ? "#10b981" : h.hour === max.hour ? "#f97316" : "#3f3f46"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-zinc-500 mt-2">Grønne søyler er de tre billigste timene – lurt tidspunkt for vaskemaskin, oppvask og elbillading.</div>
        </>
      )}
    </Card>
  );
};
